// Mock data para Interfaz V2.
//
// Acá vive todo el contenido de prueba que alimenta la nueva UI de
// generación: colecciones de presets, presets individuales, productos
// del usuario, etc. Es 100% data hardcodeada para iterar visualmente
// sin tocar Supabase. Cuando se valide la dirección, todo lo que está
// acá se reemplaza por queries reales.
//
// Las imágenes usan Unsplash (URLs estables con parámetros de tamaño)
// para tener variedad sin subir assets al repo. Si Unsplash cambia
// alguna foto, simplemente queda otra random — no es problema para
// mockups.

export interface PresetV2 {
  id: string;
  title: string;
  /**
   * URL del preview optimizado (thumbnail ≤400px) — se usa en los grids
   * y carruseles de la home. Carga lazy en los tiles.
   */
  imageUrl: string;
  /**
   * URL del preview HD (~1080px) — se usa cuando el preset se muestra en
   * grande (preview de "Utilizar", hero del sheet, etc.). Si no se
   * provee, los renders en grande caen al `imageUrl` opt.
   */
  imageUrlFull?: string;
  /** Descripción corta opcional, sale en la vista expandida. */
  description?: string;
  /**
   * Tipo de preset:
   *   • 'template' (default) — plantilla de escena tradicional. El user
   *     pone solo el producto y la pose/modelo viene "implicito" en la
   *     foto del template.
   *   • 'design' — plantilla de "Diseña tu imagen" para rubros de moda /
   *     belleza: la imagen del preset es la POSE, y el config panel
   *     muestra opciones extras (Modelo + Fondo + Formato).
   *   • 'design-modelo' — modo nuevo "Imagen con modelo": el user elige
   *     pose(s) + fondo + modelo. Manda al backend con `modo:
   *     "personalizado"` + `composicion: "modelo"` (mismo endpoint que
   *     PruebaNuevaInterfaz, no como template).
   */
  /**
   *   • 'design-auto' — modo "Generar imágenes automáticamente": mismo
   *     flow visual que `design-modelo` pero SIN selección de
   *     modelo/pose/fondo. El backend recibe `modo: "rapido"` +
   *     `composicion: "producto"` + `background_mode: "ia"`, dejando
   *     que IA decida la escena entera (mismo payload que el tab
   *     "automatico" de PruebaNuevaInterfaz).
   *   • 'catalogo-ia' — modo Catálogo IA: el user elige productos +
   *     un conjunto de "vistas" (poses con-modelo o ángulos solo-
   *     producto). El sheet dispara una llamada V3 por cada (vista,
   *     producto) compatible, todas en paralelo, todas con fondo
   *     blanco y modelo IA cuando aplica.
   */
  kind?:
    | 'template'
    | 'design'
    | 'design-modelo'
    | 'design-auto'
    | 'catalogo-ia'
    | 'asistente'
    | 'anuncio'
    // Modo "Video con influencer" (dev-only). Pantalla 2 = configurar video +
    // selección de gancho. Reusa el layout de plantilla-video del
    // `ConfigurePanel` + la sección `components/GanchoSection`.
    | 'video-influencer';
  /**
   * Lista de `model_view` (de la tabla `productos`) para los cuales el
   * template está pensado — ej. ["torso", "cuerpo_completo", "piernas"]
   * para escenas de cuerpo. Cuando se elige el template y el rubro del
   * user califica, el product picker filtra a "Sugeridos" los productos
   * cuyo `modelView` matchee este array.
   */
  tiposProductoSugeridos?: string[];
  /**
   * Tipos de input que la plantilla acepta en backend
   * (`templates.inputs_aceptados`). Valores típicos: "producto", "modelo".
   * Si el array existe y NO incluye "modelo", la UI esconde la sección
   * de Modelo en el configure (la plantilla genera solo con producto).
   * Si está undefined/missing, asumimos que acepta todo (back-compat).
   */
  inputsAceptados?: string[];
  /**
   * Tipo de salida de la plantilla — `'imagen' | 'imagen_pro' | 'video'`.
   * Si es video, el preview se renderiza con un `<video>` autoplay loop
   * + badge de play. Las plantillas de imagen siguen usando `<img>`.
   * Para legacy presets sin output_type (design/design-modelo) queda
   * undefined y la UI cae al comportamiento de imagen.
   */
  outputType?: 'imagen' | 'imagen_pro' | 'video';
  /**
   * Cantidad de personas que aparecen en la imagen del preset. Solo se
   * setea para los presets generados a partir de poses ('design'/
   * e-commerce). Cuando es > 1, el multi-select de plantillas queda
   * bloqueado para este preset — el flow no puede mezclar poses
   * multi-persona con otras.
   */
  personCount?: number;
  /**
   * Pricing: cuántos créditos cuesta una unidad. Default 1 en la DB.
   */
  creditosPorUnidad?: number;
  /**
   * Qué cuenta como unidad para el costo: `generacion` (1×), `imagen`
   * (1× por imagen del job), `segundo` (1× por segundo de video).
   */
  unidadConsumo?: 'generacion' | 'imagen' | 'segundo';
  /**
   * Duración del video en segundos. Solo aplica si `outputType==='video'`.
   * Es la única fuente para billing cuando `unidadConsumo === 'segundo'`.
   */
  durationSeconds?: number | null;
}

export interface CollectionV2 {
  id: string;
  title: string;
  /** Subtítulo opcional con la "vibe" de la colección. */
  subtitle?: string;
  presets: PresetV2[];
}

export interface MockProductV2 {
  id: string;
  name: string;
  imageUrl: string;
  /**
   * `model_view` del producto padre — indica en qué parte del modelo
   * "vive" el producto (ej. "torso", "calzado", "cuerpo_completo",
   * "piernas", "cabeza", "accesorio_cuello", "manos", "lentes", "aros").
   * Se usa para sugerir productos según el template elegido.
   * null/empty = no clasificado.
   */
  modelView?: string | null;
}

// Helper para evitar repetir el path de Unsplash. `seed` permite que la
// misma "tarjeta" siempre cargue la misma imagen entre renders.
const u = (seed: string, w = 600, h = 800) =>
  `https://images.unsplash.com/${seed}?auto=format&fit=crop&w=${w}&h=${h}&q=80`;

export const COLLECTIONS_V2: CollectionV2[] = [
  {
    id: 'looks-invierno',
    title: 'Looks de invierno',
    subtitle: 'Texturas pesadas, tonos cálidos, escenarios fríos',
    presets: [
      { id: 'wi-1', title: 'Calle parisina', imageUrl: u('photo-1483985988355-763728e1935b') },
      { id: 'wi-2', title: 'Cafetería neoyorquina', imageUrl: u('photo-1551803091-e20673f15770') },
      { id: 'wi-3', title: 'Estudio luz natural', imageUrl: u('photo-1490481651871-ab68de25d43d') },
      { id: 'wi-4', title: 'Bosque nevado', imageUrl: u('photo-1518770660439-4636190af475') },
      { id: 'wi-5', title: 'Montaña al amanecer', imageUrl: u('photo-1551803091-e20673f15770') },
      { id: 'wi-6', title: 'Mercado europeo', imageUrl: u('photo-1488161628813-04466f872be2') },
      { id: 'wi-7', title: 'Loft minimalista', imageUrl: u('photo-1487222477894-8943e31ef7b2') },
      { id: 'wi-8', title: 'Sala con chimenea', imageUrl: u('photo-1493663284031-b7e3aefcae8e') },
    ],
  },
  {
    id: 'estilos-minimalistas',
    title: 'Estilos minimalistas',
    subtitle: 'Composiciones limpias, paleta reducida, foco en el producto',
    presets: [
      { id: 'mn-1', title: 'Fondo crema lateral', imageUrl: u('photo-1505740420928-5e560c06d30e') },
      { id: 'mn-2', title: 'Estudio blanco puro', imageUrl: u('photo-1556909114-44e3e9699e2b') },
      { id: 'mn-3', title: 'Hormigón pulido', imageUrl: u('photo-1604176354204-9268737828e4') },
      { id: 'mn-4', title: 'Mesa de madera clara', imageUrl: u('photo-1542291026-7eec264c27ff') },
      { id: 'mn-5', title: 'Pared beige sombras', imageUrl: u('photo-1551488831-00ddcb6c6bd3') },
      { id: 'mn-6', title: 'Backdrop terracota', imageUrl: u('photo-1542291026-7eec264c27ff') },
    ],
  },
  {
    id: 'atmosferas-urbanas',
    title: 'Atmósferas urbanas',
    subtitle: 'Calles, terrazas, transporte público, vida de ciudad',
    presets: [
      { id: 'ur-1', title: 'Skyline al atardecer', imageUrl: u('photo-1518391846015-55a9cc003b25') },
      { id: 'ur-2', title: 'Subte iluminado', imageUrl: u('photo-1581375074612-d1fd0e661aeb') },
      { id: 'ur-3', title: 'Terraza con plantas', imageUrl: u('photo-1517248135467-4c7edcad34c4') },
      { id: 'ur-4', title: 'Esquina con neón', imageUrl: u('photo-1503376780353-7e6692767b70') },
      { id: 'ur-5', title: 'Azotea soleada', imageUrl: u('photo-1494522855154-9297ac14b55f') },
      { id: 'ur-6', title: 'Callejón de adoquines', imageUrl: u('photo-1519955266818-0231b63402b5') },
      { id: 'ur-7', title: 'Plaza central', imageUrl: u('photo-1502602898657-3e91760cbb34') },
      { id: 'ur-8', title: 'Puente al amanecer', imageUrl: u('photo-1480714378408-67cf0d13bc1b') },
      { id: 'ur-9', title: 'Galería comercial', imageUrl: u('photo-1542228262-3d663b306a53') },
      { id: 'ur-10', title: 'Bar con luces cálidas', imageUrl: u('photo-1514933651103-005eec06c04b') },
    ],
  },
  {
    id: 'estetica-natural',
    title: 'Estética natural',
    subtitle: 'Luz suave, plantas, materiales orgánicos',
    presets: [
      { id: 'na-1', title: 'Playa al amanecer', imageUrl: u('photo-1507525428034-b723cf961d3e') },
      { id: 'na-2', title: 'Sendero entre árboles', imageUrl: u('photo-1448375240586-882707db888b') },
      { id: 'na-3', title: 'Pradera con flores', imageUrl: u('photo-1500382017468-9049fed747ef') },
      { id: 'na-4', title: 'Cabaña en el bosque', imageUrl: u('photo-1542038784456-1ea8e935640e') },
      { id: 'na-5', title: 'Lago al atardecer', imageUrl: u('photo-1470770841072-f978cf4d019e') },
      { id: 'na-6', title: 'Sol entre hojas', imageUrl: u('photo-1502082553048-f009c37129b9') },
    ],
  },
  {
    id: 'escenarios-elegantes',
    title: 'Escenarios elegantes',
    subtitle: 'Interiores de alta gama, luz suave, composiciones cuidadas',
    presets: [
      { id: 'el-1', title: 'Hall de hotel', imageUrl: u('photo-1564501049412-61c2a3083791') },
      { id: 'el-2', title: 'Salón clásico', imageUrl: u('photo-1517840901100-8179e982acb7') },
      { id: 'el-3', title: 'Vestidor con espejo', imageUrl: u('photo-1556228720-195a672e8a03') },
      { id: 'el-4', title: 'Biblioteca de roble', imageUrl: u('photo-1568667256549-094345857637') },
      { id: 'el-5', title: 'Comedor con velas', imageUrl: u('photo-1551218808-94e220e084d2') },
      { id: 'el-6', title: 'Bar art déco', imageUrl: u('photo-1572116469696-31de0f17cc34') },
      { id: 'el-7', title: 'Penthouse con vista', imageUrl: u('photo-1502672260266-1c1ef2d93688') },
    ],
  },
];

// Mock de modelos pre-armados que el usuario puede elegir. Cada uno tiene
// una imagen de cuerpo completo sobre fondo blanco/neutro. Cuando esto se
// conecte a real, viene de una tabla `models` propia del usuario o del
// catálogo del sistema.
export interface MockModelV2 {
  id: string;
  name: string;
  /** Imagen de cuerpo completo. Idealmente fondo blanco. */
  imageUrl: string;
  /** Hint corto (género/edad) — aparece debajo del nombre. */
  hint?: string;
}

export const MOCK_MODELS_V2: MockModelV2[] = [
  { id: 'm-1', name: 'Lucía', hint: 'Mujer · 25', imageUrl: u('photo-1488161628813-04466f872be2', 400, 600) },
  { id: 'm-2', name: 'Camila', hint: 'Mujer · 28', imageUrl: u('photo-1554151228-14d9def656e4', 400, 600) },
  { id: 'm-3', name: 'Mateo', hint: 'Hombre · 30', imageUrl: u('photo-1507003211169-0a1dd7228f2d', 400, 600) },
  { id: 'm-4', name: 'Sofía', hint: 'Mujer · 22', imageUrl: u('photo-1531123897727-8f129e1688ce', 400, 600) },
  { id: 'm-5', name: 'Tomás', hint: 'Hombre · 27', imageUrl: u('photo-1500648767791-00dcc994a43e', 400, 600) },
  { id: 'm-6', name: 'Valentina', hint: 'Mujer · 32', imageUrl: u('photo-1544005313-94ddf0286df2', 400, 600) },
];

export const MOCK_USER_PRODUCTS: MockProductV2[] = [
  { id: 'p-1', name: 'Top encaje marrón', imageUrl: u('photo-1539109136881-3be0616acf4b', 400, 400) },
  { id: 'p-2', name: 'Pantalón sastre negro', imageUrl: u('photo-1594633312681-425c7b97ccd1', 400, 400) },
  { id: 'p-3', name: 'Botas de cuero', imageUrl: u('photo-1542838132-92c53300491e', 400, 400) },
  { id: 'p-4', name: 'Zapatillas marrones', imageUrl: u('photo-1542291026-7eec264c27ff', 400, 400) },
  { id: 'p-5', name: 'Blazer beige', imageUrl: u('photo-1591047139829-d91aecb6caea', 400, 400) },
  { id: 'p-6', name: 'Vestido calado', imageUrl: u('photo-1572804013309-59a88b7e92f1', 400, 400) },
  { id: 'p-7', name: 'Blusa rosa', imageUrl: u('photo-1485518882345-15568b007407', 400, 400) },
];

// Configuración: opciones que aparecen en el último paso (después de
// elegir preset + producto). Cada opción tiene un icono visual sutil
// como acompañamiento. Más adelante esto se conecta a la lógica real
// del agente de generación.
export interface ConfigOptionV2 {
  id: string;
  label: string;
  values: string[];
}

export const CONFIG_OPTIONS_V2: ConfigOptionV2[] = [
  {
    id: 'modelo',
    label: 'Modelo',
    values: [
      'Sugerido',
      'Mujer adulta',
      'Mujer joven',
      'Hombre adulto',
      'Hombre joven',
      'Modelo personalizado',
    ],
  },
  {
    id: 'fondo',
    label: 'Fondo',
    values: [
      'Sugerido',
      'Liso blanco',
      'Liso negro',
      'Estudio',
      'Calle / Urbano',
      'Naturaleza',
      'Interior',
    ],
  },
  {
    id: 'formato',
    label: 'Formato',
    values: ['Cuadrado', 'Vertical', 'Horizontal', 'Story', 'Banner'],
  },
];

// Opciones de formato — copiadas de PruebaNuevaInterfaz para que el picker
// de Interfaz V2 muestre los mismos formatos canónicos con sus previews.
// Las imágenes viven en supabase storage; nombres incluyen aspect ratio
// entre paréntesis para que el user vea de qué se trata cada uno.
export interface FormatOptionV2 {
  id: string;
  name: string;
  imageUrl: string;
}

const FORMAT_BASE_URL =
  'https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/formatos';

export const FORMAT_OPTIONS_V2: FormatOptionV2[] = [
  {
    id: 'post',
    name: 'Posteo (4:5)',
    imageUrl: `${FORMAT_BASE_URL}/Formato_Posteo.jpeg`,
  },
  {
    id: 'story',
    name: 'Historias/Reels (9:16)',
    imageUrl: `${FORMAT_BASE_URL}/Formato_Historia.jpeg`,
  },
  {
    id: 'square',
    name: 'Cuadrado (1:1)',
    imageUrl: `${FORMAT_BASE_URL}/Formato_Cuadrado.jpeg`,
  },
  {
    id: 'portrait',
    name: 'Retrato (2:3)',
    imageUrl: `${FORMAT_BASE_URL}/Formato_Retrato.jpeg`,
  },
  {
    id: 'banner',
    name: 'Banner Web (16:9)',
    imageUrl: `${FORMAT_BASE_URL}/Formato_BannerWeb.png`,
  },
  {
    id: 'editorial',
    name: 'Editorial (3:2)',
    imageUrl: `${FORMAT_BASE_URL}/Formato_Editorial.png`,
  },
  {
    id: 'billboard',
    name: 'Billboard (21:9)',
    imageUrl: `${FORMAT_BASE_URL}/Formato_Billboard.png`,
  },
];

/** Default cuando el user no eligió formato. */
export const DEFAULT_FORMAT_ID = 'square';

export const getFormatLabel = (id: string | null): string => {
  if (!id) return 'Cuadrado (1:1)';
  return FORMAT_OPTIONS_V2.find((f) => f.id === id)?.name || 'Cuadrado (1:1)';
};

// Frases sugeridas para el botón "sorprendeme" — al tocarlo, una se
// elige al random y se inserta en el textarea como prompt inicial.
export const SURPRISE_PROMPTS_V2: string[] = [
  'Una foto editorial estilo Vogue, con luz dramática y atmósfera contemplativa',
  'Lifestyle en un café de barrio europeo, hora dorada, cámara cinematográfica',
  'Producto sobre superficie de mármol pulido con sombras suaves de mediodía',
  'Modelo caminando por una calle empedrada al atardecer, look casual elegante',
  'Composición minimalista, fondo neutro, foco completo en el producto',
  'Atmósfera invernal nórdica, tonos fríos, textura de niebla suave',
];
